import { ToastMessages } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { SetErrorFunction } from '@/pages/Admin/HierarchyOfRetailers/Locations/Types';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { DetailedApiError } from '@/utilities/api/DetailedApiError';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RetailerEndpoint, RetailerLocationEndpoint } from '@api/endpoints';
import { useMutation, useQuery, useQueryClient } from 'react-query';


const useAddRetailerLocation = (onClose: (args: boolean) => void, setError: SetErrorFunction) => {
  const { openSnackbar: showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutate: addRetailerLocation, isLoading } = useMutation(
    (args: RetailerLocationEndpoint.Create.Request) =>
      RetailerApi.addRetailerLocation(args),
    {
      onSuccess: async () => {
        onClose(false);
        await queryClient.invalidateQueries(
          QueryKeys.GET_RETAILER_LOCATION_LIST,
        );
        showSnackbar(ToastMessages.addLocationSuccess);
      },
      onError: (error: DetailedApiError) => {
        setError && setError((prev) => ({
          ...(prev),
          serverError: error.message,
        }));
      },
    },
  );

  return { addRetailerLocation, isAddLocationLoading: isLoading };
};

const useGetRetailerLocations = (
  id: string,
  query: RetailerEndpoint.List.Query = {},
) => {
  const queryClient = useQueryClient();
  const { data: retailerLocations, isLoading } = useQuery(
    [QueryKeys.GET_RETAILER_LOCATION_LIST, id, JSON.stringify(query)],
    () => RetailerApi.getLocations(id, query),
    { enabled: !!id },
  );

  const invalidateRetailerLocations = async () => {
    await queryClient.invalidateQueries([QueryKeys.GET_RETAILER_LOCATION_LIST]);
  };

  return { retailerLocations, invalidateRetailerLocations, isLoading };
};

const useGetLocationMembers = (id: string, search: string) => {
  const { data: locationMembers, isLoading } = useQuery(
    [QueryKeys.GET_LOCATION_MEMBERS, id, search],
    () => RetailerApi.getLocationMembers(id, search),
  );

  return { locationMembers, isLoading };
};

const useGetLocationById = (id: string) => {
  const { data: location, isLoading } = useQuery(
    [QueryKeys.GET_LOCATION_BY_ID, id],
    () => RetailerApi.getRetailerLocationById(id),
  );

  const queryClient = useQueryClient();
  const invalidateRetailerLocation = async () => {
    await queryClient.invalidateQueries([QueryKeys.GET_LOCATION_BY_ID]);
  };

  return { location, invalidateRetailerLocation, isLoading };
};

const useEditRetailerLocation = (
  id: string,
  onClose?: (args: boolean) => void,
) => {
  const queryClient = useQueryClient();
  const { mutate: updateRetailerLocation, isLoading } = useMutation(
    (args: RetailerLocationEndpoint.Update.Request) =>
      RetailerApi.updateRetailerLocation(id, args),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_LOCATION_BY_ID);
        await queryClient.invalidateQueries(
          QueryKeys.GET_RETAILER_LOCATION_LIST,
        );
        onClose && onClose(false);
      },
    },
  );

  return { updateRetailerLocation, isLoading };
};

const useUpdateLocation = (id: string, onClose: () => void, setError: SetErrorFunction) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { mutate: updateLocation, isLoading } = useMutation(
    (args: RetailerLocationEndpoint.Update.Request) =>
      RetailerApi.updateRetailerLocation(id, args),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_LOCATION_BY_ID);
        openSnackbar(ToastMessages.updateLocationSuccess);
        onClose();
      },
      onError: (error: DetailedApiError) => {
        setError((prev) => ({
          ...prev,
          serverError: error.message,
        }));
      },
    },
  );

  return { updateLocation, isLoading };
};

export {
  useAddRetailerLocation,
  useGetRetailerLocations,
  useGetLocationMembers,
  useGetLocationById,
  useEditRetailerLocation,
  useUpdateLocation,
};
